import { tx } from "@/i18n/tx";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Check } from "lucide-react";

const AREAS = [
  ["emozioni", tx("Emozioni")], ["relazioni", tx("Relazioni")], ["autostima", tx("Autostima")],
  ["cambiamento", tx("Cambiamento")], ["direzione", tx("Direzione di vita")], ["creativita", tx("Creativita")],
  ["spiritualita", tx("Spiritualita")], ["calma", tx("Calma interiore")],
];
const TONES = [
  ["delicato", tx("Delicato e rassicurante")], ["diretto", tx("Diretto e concreto")],
  ["simbolico", tx("Simbolico e spirituale")], ["equilibrato", tx("Equilibrato")],
];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    display_name: user?.display_name || user?.name || "",
    reason: "", area: "", mood: "", tone: "equilibrato", reminder_time: "20:00", consent: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const steps = [
    {
      title: tx("Come desideri essere chiamata/o?"),
      valid: form.display_name.trim().length > 0,
      body: (
        <input
          data-testid="onb-name"
          value={form.display_name} onChange={(e) => set("display_name", e.target.value)}
          className="w-full rounded-2xl bg-white/60 border border-anima-ink/10 px-4 py-3.5 outline-none focus:border-anima-sage font-serif text-2xl"
          placeholder={tx("Il tuo nome")}
        />
      ),
    },
    {
      title: tx("Cosa ti porta qui, oggi?"),
      valid: true,
      body: (
        <textarea
          data-testid="onb-reason"
          value={form.reason} onChange={(e) => set("reason", e.target.value)}
          rows={4}
          className="w-full rounded-2xl bg-white/60 border border-anima-ink/10 px-4 py-3.5 outline-none focus:border-anima-sage font-serif text-lg leading-relaxed"
          placeholder={tx("Anche poche parole vanno bene...")}
        />
      ),
    },
    {
      title: tx("Cosa desideri esplorare?"),
      valid: !!form.area,
      body: (
        <div className="grid grid-cols-2 gap-3">
          {AREAS.map(([k, l]) => (
            <button
              key={k} data-testid={`onb-area-${k}`}
              onClick={() => set("area", k)}
              className={`rounded-2xl py-4 px-3 text-sm border transition-colors duration-300 ${
                form.area === k ? "bg-anima-sage/30 border-anima-sage text-anima-ink" : "bg-white/50 border-anima-ink/10 text-anima-muted"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      ),
    },
    {
      title: tx("Come ti senti in questo periodo?"),
      valid: true,
      body: (
        <div className="flex flex-wrap gap-2.5">
          {["serena/o", "in cerca", "stanca/o", "in cambiamento", "sensibile", "fiduciosa/o", "confusa/o", "grata/o"].map((m) => (
            <button
              key={m} data-testid={`onb-mood-${m}`}
              onClick={() => set("mood", m)}
              className={`rounded-full py-2.5 px-4 text-sm border transition-colors duration-300 ${
                form.mood === m ? "bg-anima-pink border-anima-pink text-anima-ink" : "bg-white/50 border-anima-ink/10 text-anima-muted"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      ),
    },
    {
      title: tx("Quale tono preferisci?"),
      valid: !!form.tone,
      body: (
        <div className="space-y-3">
          {TONES.map(([k, l]) => (
            <button
              key={k} data-testid={`onb-tone-${k}`}
              onClick={() => set("tone", k)}
              className={`w-full text-left rounded-2xl py-4 px-4 border transition-colors duration-300 flex items-center justify-between ${
                form.tone === k ? "bg-anima-sage/30 border-anima-sage" : "bg-white/50 border-anima-ink/10"
              }`}
            >
              <span className="font-serif text-lg text-anima-ink">{l}</span>
              {form.tone === k && <Check size={18} className="text-anima-sagedeep" />}
            </button>
          ))}
        </div>
      ),
    },
    {
      title: tx("A che ora vuoi il promemoria gentile?"),
      valid: true,
      body: (
        <div>
          <input
            data-testid="onb-time" type="time" value={form.reminder_time}
            onChange={(e) => set("reminder_time", e.target.value)}
            className="w-full rounded-2xl bg-white/60 border border-anima-ink/10 px-4 py-3.5 outline-none focus:border-anima-sage font-serif text-2xl"
          />
          <p className="text-sm text-anima-muted mt-3">{tx("Un invito discreto, mai un obbligo. Potrai modificarlo o disattivarlo quando vuoi.")}</p>
        </div>
      ),
    },
    {
      title: tx("Privacy, finalita e limiti"),
      valid: form.consent,
      body: (
        <div>
          <div className="anima-card rounded-2xl p-5 text-sm text-anima-muted leading-relaxed space-y-3 max-h-64 overflow-y-auto no-scrollbar">
            <p><b className="text-anima-ink">{tx("A cosa serve.")}</b>{tx("Ascolta Anima e uno strumento di autoriflessione e supporto alla consapevolezza. Non offre diagnosi, psicoterapia, predizioni o verita assolute e non sostituisce professionisti sanitari o psicologici.")}</p>
            <p><b className="text-anima-ink">{tx("La tua privacy.")}</b>{tx("I tuoi dati personali sono separati dai contenuti del diario. Le tue riflessioni sono private, non vengono condivise con altri utenti ne usate per pubblicita. Puoi esportare o cancellare i tuoi dati in ogni momento.")}</p>
            <p><b className="text-anima-ink">{tx("In caso di emergenza.")}</b>{tx("Se stai vivendo un momento di rischio, l'app ti invitera a contattare subito il 112 o una persona di fiducia.")}</p>
          </div>
          <button
            data-testid="onb-consent"
            onClick={() => set("consent", !form.consent)}
            className="mt-4 w-full flex items-center gap-3 rounded-2xl border border-anima-ink/10 bg-white/50 px-4 py-3.5"
          >
            <span className={`w-5 h-5 rounded-md border flex items-center justify-center ${form.consent ? "bg-anima-sagedeep border-anima-sagedeep" : "border-anima-ink/25"}`}>
              {form.consent && <Check size={14} className="text-white" />}
            </span>
            <span className="text-sm text-anima-ink text-left">{tx("Ho letto e acconsento al trattamento dei miei dati.")}</span>
          </button>
        </div>
      ),
    },
  ];

  const cur = steps[step];
  const isLast = step === steps.length - 1;

  const next = async () => {
    if (!cur.valid) return;
    if (!isLast) { setStep(step + 1); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding", form);
      setUser(data);
      toast.success(tx("Il tuo spazio e pronto"));
      navigate("/app");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="anima-backdrop min-h-screen w-full flex justify-center">
      <div className="w-full max-w-md px-7 py-10 flex flex-col min-h-screen">
        <div className="flex gap-1.5 mb-10">
          {/* Lista statica di indicatori di passo: mai riordinata, indice sicuro come key */}
          {steps.map((_, i) => (
            <div key={`step-${i}`} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-anima-sagedeep" : "bg-anima-ink/10"}`} />
          ))}
        </div>

        <div className="flex items-center gap-1.5 text-[11px] text-anima-muted mb-6">
          <ShieldCheck size={13} />{tx("Passo")} {step + 1} / {steps.length}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4 }}
            className="flex-1"
          >
            <h2 className="font-serif text-3xl text-anima-ink mb-7 leading-tight">{cur.title}</h2>
            {cur.body}
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex gap-3">
          {step > 0 && (
            <button data-testid="onb-back" onClick={() => setStep(step - 1)} className="rounded-full border border-anima-ink/15 text-anima-ink px-6 py-4">{tx("Indietro")}</button>
          )}
          <button
            data-testid="onb-next" onClick={next} disabled={!cur.valid || busy}
            className="flex-1 rounded-full bg-anima-sagedeep text-anima-ivory py-4 font-medium active:scale-95 transition-transform duration-200 disabled:opacity-40"
          >
            {isLast ? (busy ? tx("Un momento...") : tx("Entra nel percorso")) : tx("Continua")}
          </button>
        </div>
      </div>
    </div>
  );
}
